import {
  ActionEvents,
  PmfDisappointment,
  PmfSurvey,
  PmfSurveyFreeTextEvent,
  UsageStatisticsEntry,
  User,
  UserPropertiesKeys,
} from '@griply/types';
import { useCallback } from 'react';

export type PmfSurveyTrack = {
  (
    event: ActionEvents.PmfSurveyHowWouldYouFeel,
    props: { answer: PmfDisappointment },
  ): void;
  (event: PmfSurveyFreeTextEvent, props: { answer: string }): void;
};

export type UpdatePmfSurvey = (fields: {
  pmfSurvey: Partial<PmfSurvey>;
  usageStatistics?: UsageStatisticsEntry[];
}) => void;

export type UseSubmitPmfSurveyOptions = {
  user: User | null | undefined;
  track: PmfSurveyTrack;
  setUserProperties: (properties: {
    [UserPropertiesKeys.PmfDisappointment]: PmfDisappointment;
  }) => void;
  updateUser: UpdatePmfSurvey;
};

export const submitPmfDisappointment = (
  {
    track,
    setUserProperties,
    updateUser,
  }: Pick<
    UseSubmitPmfSurveyOptions,
    'track' | 'setUserProperties' | 'updateUser'
  >,
  value: PmfDisappointment,
) => {
  track(ActionEvents.PmfSurveyHowWouldYouFeel, { answer: value });
  setUserProperties({ [UserPropertiesKeys.PmfDisappointment]: value });
  updateUser({
    pmfSurvey: { surveyCompleted: true, lastTimeShown: new Date() },
    usageStatistics: [],
  });
};

export const submitPmfText = (
  { track }: Pick<UseSubmitPmfSurveyOptions, 'track'>,
  event: PmfSurveyFreeTextEvent,
  value: string,
) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return;
  }
  track(event, { answer: trimmed });
};

export const submitPmfDismissal = ({
  user,
  updateUser,
}: Pick<UseSubmitPmfSurveyOptions, 'user' | 'updateUser'>) => {
  updateUser({
    pmfSurvey: {
      surveyShownCount: (user?.pmfSurvey?.surveyShownCount ?? 0) + 1,
      lastTimeShown: new Date(),
    },
  });
};

export const useSubmitPmfSurvey = ({
  user,
  track,
  setUserProperties,
  updateUser,
}: UseSubmitPmfSurveyOptions) => {
  const answerDisappointment = useCallback(
    (value: PmfDisappointment) =>
      submitPmfDisappointment({ track, setUserProperties, updateUser }, value),
    [track, setUserProperties, updateUser],
  );

  const answerText = useCallback(
    (event: PmfSurveyFreeTextEvent, value: string) =>
      submitPmfText({ track }, event, value),
    [track],
  );

  const dismiss = useCallback(
    () => submitPmfDismissal({ user, updateUser }),
    [user, updateUser],
  );

  return { answerDisappointment, answerText, dismiss };
};
